/**
 * client/src/admin/Generations.tsx — "are they 100% done, or regenerate, or last user".
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE TWO NUMBERS ARE LABELLED DIFFERENTLY BECAUSE THEY ARE DIFFERENT CLAIMS.
 *
 * **"38 of 41 pages have audio"** is cheap, always shown, and means *a `page_audio` row exists*. It
 * is NOT "done": `narration/plan.ts` documents at length that a stale artifact key or a missing file
 * still leaves that row standing, so the count can be complete over audio nobody can play.
 *
 * **"Verified"** is the exact answer and costs one batched `planPages()` over the whole edition, so
 * it is on a button and never cached. That number — zero pages needing the GPU and zero needing a
 * re-stitch — is the only thing on this screen that means 100%.
 *
 * There is deliberately no `books.narratedPages` column behind either. `plan.ts:12-25` is the whole
 * argument: a cached freshness flag has to be invalidated by the importer, the CLI, the realigner,
 * the renormaliser and the pronunciation editor, *"and the ONE missed path reports 'fresh' over
 * deleted prose"*. A percentage attached to that bug is worse than the bug.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **No new CSS.** Every class here already exists in `client/src/ui/app.css`. AGENTS.md: never
 * hardcode a hex, and the way not to is to not write a stylesheet.
 */

import { useCallback, useState, type ReactElement } from "react";

import { timeAgo } from "../App.tsx";
import { ApiError, api } from "../lib/api.ts";

/* ── the wire ─────────────────────────────────────────────────────────────── */

export interface GenerationSweep {
  state: string;
  cursorOrd: number;
  gpuSecSpent: number;
  startedBy: string;
  startedAt: number;
  updatedAt: number;
}

export interface GenerationBook {
  bookId: string;
  title: string;
  author: string | null;
  owner: { handle: string | null; email: string | null };
  editionId: string | null;
  editionState: string | null;
  structureConfirmed: boolean;
  narratorVoiceId: string;
  narratorLabel: string | null;
  /** Every voice with an active clip on this edition. One today; the cast makes it more (P7d). */
  voices: string[];
  pagesTotal: number;
  pagesAudible: number;
  pagesWithAudio: number;
  clips: number;
  clipBytes: number;
  artifacts: number;
  artifactBytes: number;
  gpuSec: number;
  lastGeneratedAt: number | null;
  lastHeard: { at: number; actorKey: string; who: string | null; pageId: string | null; playing: boolean } | null;
  sweep: GenerationSweep | null;
  jobs: { queued: number; running: number; failed: number };
}

export interface GenerationsResponse {
  now: number;
  narrator: { voiceId: string; label: string | null; rev: number; variant: string };
  totals: {
    books: number;
    pagesAudible: number;
    pagesWithAudio: number;
    clips: number;
    gpuSec: number;
    bytes: number;
  };
  jobs: { queued: number; running: number; failed: number };
  books: GenerationBook[];
}

/** `VerifiedPage` from `server/src/narration/coverage.ts`. Only the unfinished ones travel. */
interface VerifyPage {
  pageId: string;
  ord: number;
  chapterOrd: number;
  state: "ready" | "stitch" | "gpu" | "nothing-to-say" | "failed";
  /** The planner's own live state, so `queued` and `building` are not lost behind the verdict. */
  planState: string;
  missing: number;
  estGpuSec: number;
}

/** `VerifiedCoverage`, plus the price and the sentence the route adds. */
/**
 * What `POST /admin/generations/:bookId/fill` answers, priced or applied.
 *
 * The same object either way, with `applied` flipped — the property that makes the preview
 * trustworthy, and the shape every priced button in this product already uses.
 */
interface FillReport {
  applied: boolean;
  pages: number;
  needGpu: number;
  needStitch: number;
  estGpuSec: number;
  budget: { ceilingMs: number; spentMs: number; remainingMs: number; exhausted: boolean; jobs: number };
  enqueued: { pageId: string; jobId: string; lane: string; kind: string; estGpuSec: number }[];
  skipped: { pageId: string; why: string }[];
  summary: string;
  verify: VerifyReport;
}

interface VerifyReport {
  bookId: string;
  editionId: string;
  voiceId: string;
  checkedAt: number;
  tookMs: number;
  complete: boolean;
  counts: {
    total: number;
    audible: number;
    ready: number;
    stitch: number;
    gpu: number;
    nothingToSay: number;
    failed: number;
  };
  staleIndex: number;
  estGpuSec: number;
  summary: string;
  pages: VerifyPage[];
  pagesOmitted: number;
}

/* ── the screen ───────────────────────────────────────────────────────────── */

export function Generations({
  data,
  error,
  loading,
  onRefresh,
}: {
  data: GenerationsResponse | null;
  error: string | null;
  loading: boolean;
  onRefresh: () => void;
}): ReactElement {
  const [sort, setSort] = useState<"gpu" | "gap" | "heard">("gap");

  if (error !== null) return <section className="ab-panel ab-panel-bad">{error}</section>;
  if (data === null) return <section className="ab-panel">{loading ? "Counting the library…" : "No answer."}</section>;

  const books = [...data.books].sort((a, b) => {
    if (sort === "gpu") return b.gpuSec - a.gpuSec;
    if (sort === "heard") return (b.lastHeard?.at ?? 0) - (a.lastHeard?.at ?? 0);
    // "gap" — the books furthest from finished, first. This is the default because it is the answer
    // to "what should I press next", which is the question the screen exists for.
    return gapOf(b) - gapOf(a);
  });

  return (
    <>
      <section className="ab-panel">
        <h2 className="ab-h2">The library, as generated</h2>
        <dl className="ab-facts">
          <div>
            <dt>Books</dt>
            <dd>{data.totals.books}</dd>
          </div>
          <div>
            <dt>Pages with audio</dt>
            <dd>
              {data.totals.pagesWithAudio} of {data.totals.pagesAudible}
            </dd>
          </div>
          <div>
            <dt>Clips</dt>
            <dd>{data.totals.clips.toLocaleString()}</dd>
          </div>
          <div>
            <dt>GPU spent</dt>
            <dd>{describeGpu(data.totals.gpuSec)}</dd>
          </div>
          <div>
            <dt>Audio on disk</dt>
            <dd>{describeBytes(data.totals.bytes)}</dd>
          </div>
          <div>
            <dt>Narrator</dt>
            <dd>
              {data.narrator.label ?? data.narrator.voiceId} <span className="ab-hint-quiet">rev {data.narrator.rev}</span>
            </dd>
          </div>
        </dl>
        {data.jobs.failed > 0 ? (
          <p className="ab-hint-bad">
            {data.jobs.failed} job{data.jobs.failed === 1 ? " has" : "s have"} failed and{" "}
            {data.jobs.failed === 1 ? "is" : "are"} waiting for a human. They are on the <strong>Backlog</strong> tab.
          </p>
        ) : null}
        <p className="ab-hint-quiet">
          <strong>“Pages with audio”</strong> counts a <code>page_audio</code> row existing. That is not the
          same as done — a stale artifact key still leaves the row standing. Press <em>Verify</em> on a book
          for the exact answer; it re-plans the whole edition and is the only number here that means 100%.
        </p>
      </section>

      <section className="ab-panel">
        <div className="ab-shelf-controls">
          <div className="ab-segment" role="group" aria-label="Sort the library">
            {(
              [
                { id: "gap", label: "Furthest from done" },
                { id: "gpu", label: "Most GPU spent" },
                { id: "heard", label: "Last listened" },
              ] as const
            ).map((s) => (
              <button
                key={s.id}
                type="button"
                className={sort === s.id ? "ab-segment-item is-on" : "ab-segment-item"}
                onClick={() => setSort(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button type="button" className="ab-btn ab-btn-sm" onClick={onRefresh} disabled={loading}>
            {loading ? "Reading…" : "Refresh"}
          </button>
        </div>

        {books.length === 0 ? (
          <p className="ab-hint">No books.</p>
        ) : (
          <table className="ab-table">
            <thead>
              <tr>
                <th scope="col">Book</th>
                <th scope="col">Audio</th>
                <th scope="col">Voices</th>
                <th scope="col">Cost</th>
                <th scope="col">Last listened</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {books.map((b) => (
                <BookRow key={b.bookId} book={b} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/** How many audible pages are still without audio. The default sort, and the "what next" number. */
const gapOf = (b: GenerationBook): number => Math.max(0, b.pagesAudible - b.pagesWithAudio);

function BookRow({ book }: { book: GenerationBook }): ReactElement {
  const [verify, setVerify] = useState<VerifyReport | null>(null);
  const [fill, setFill] = useState<FillReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setVerify(await api.get<VerifyReport>(`/admin/generations/${encodeURIComponent(book.bookId)}?verify=1`));
      setFill(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [book.bookId]);

  /**
   * The button PLAN-P7 §5.2 promised and the screen shipped without: *"a book with a `stitch` page is
   * 0.5 s of CPU from complete and the screen says so, with a button."*
   *
   * Two presses, always. The first (`apply: false`) writes nothing and returns the price against the
   * day's remaining ceiling; the second spends it. Same walk both times, so the number on the button
   * and the number in the receipt cannot disagree.
   */
  const runFill = useCallback(
    async (apply: boolean) => {
      setBusy(true);
      setError(null);
      try {
        const out = await api.post<FillReport>(`/admin/generations/${encodeURIComponent(book.bookId)}/fill`, { apply });
        setFill(out);
        setVerify(out.verify);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [book.bookId],
  );

  const gap = gapOf(book);
  const pct = book.pagesAudible === 0 ? 0 : Math.round((book.pagesWithAudio / book.pagesAudible) * 100);

  return (
    <>
      <tr>
        <td data-label="Book">
          {book.title}
          {book.author === null ? null : <span className="ab-hint-quiet"> · {book.author}</span>}
          <br />
          <span className="ab-hint-quiet">{book.owner.handle ?? book.owner.email ?? "—"}</span>
          {book.editionState !== null && book.editionState !== "ready" ? (
            <>
              {" "}
              <span className="ab-chip ab-chip-warn">{book.editionState}</span>
            </>
          ) : null}
          {!book.structureConfirmed ? (
            <>
              {" "}
              {/* LAW 6. Not cosmetic: an unconfirmed edition refuses every bulk generation, so a book
                  that will not sweep looks identical to one that simply has not been asked. */}
              <span className="ab-chip ab-chip-warn">structure unconfirmed</span>
            </>
          ) : null}
          {book.jobs.failed > 0 ? (
            <>
              {" "}
              <span className="ab-chip ab-chip-bad">
                {book.jobs.failed} failed job{book.jobs.failed === 1 ? "" : "s"}
              </span>
            </>
          ) : null}
          {book.jobs.queued + book.jobs.running > 0 ? (
            <>
              {" "}
              <span className="ab-chip ab-chip-work">
                {book.jobs.running > 0 ? `${book.jobs.running} running` : `${book.jobs.queued} queued`}
              </span>
            </>
          ) : null}
        </td>

        <td data-label="Audio" className="ab-num">
          {book.pagesWithAudio} of {book.pagesAudible}
          {book.pagesTotal > book.pagesAudible ? (
            <span className="ab-hint-quiet">
              {" "}
              {/* The denominator that made every book export as incomplete. A title page is not a gap. */}
              ({book.pagesTotal - book.pagesAudible} with nothing to read aloud)
            </span>
          ) : null}
          <span className="ab-bar" aria-hidden="true">
            <span className="ab-bar-fill" style={{ width: `${pct}%` }} />
          </span>
          {gap === 0 && book.pagesAudible > 0 ? (
            <span className="ab-hint-quiet">every audible page has audio</span>
          ) : (
            <span className="ab-hint-quiet">
              {gap} page{gap === 1 ? "" : "s"} without
            </span>
          )}
        </td>

        <td data-label="Voices">
          {book.voices.length === 0 ? (
            <span className="ab-hint-quiet">none yet</span>
          ) : (
            book.voices.map((v) => (
              <span key={v} className="ab-chip ab-chip-voice">
                {v}
              </span>
            ))
          )}
          <br />
          <span className="ab-hint-quiet">
            narrator {book.narratorLabel ?? book.narratorVoiceId}
          </span>
        </td>

        <td data-label="Cost" className="ab-num">
          {describeGpu(book.gpuSec)}
          <br />
          <span className="ab-hint-quiet">
            {book.clips.toLocaleString()} clip{book.clips === 1 ? "" : "s"} ·{" "}
            {describeBytes(book.clipBytes + book.artifactBytes)}
          </span>
          <br />
          <span className="ab-hint-quiet">
            {book.lastGeneratedAt === null ? "never generated" : `made ${timeAgo(book.lastGeneratedAt)}`}
          </span>
        </td>

        <td data-label="Last listened">
          {/* LAW 11: this is `sessions.lastHeardAt`, stamped by MEDIA BYTE REQUESTS at the proxy — a
              locked iPhone runs no timers and is very much listening. It is the only honest answer to
              "is anybody actually using this book". */}
          {book.lastHeard === null ? (
            <span className="ab-hint-quiet">nobody yet</span>
          ) : (
            <>
              {timeAgo(book.lastHeard.at)}
              <br />
              <span className="ab-hint-quiet">
                {book.lastHeard.who ?? book.lastHeard.actorKey}
                {book.lastHeard.pageId === null ? "" : ` · ${book.lastHeard.pageId}`}
                {book.lastHeard.playing ? " · playing" : ""}
              </span>
            </>
          )}
        </td>

        <td data-label="">
          <span className="ab-actions">
            <button type="button" className="ab-btn ab-btn-sm" disabled={busy || book.editionId === null} onClick={() => void run()}>
              {busy ? "Re-planning…" : "Verify"}
            </button>
            {/* Only offered once `verify` has said there is something to make: a fill button on a book
                nobody has verified would be a bulk GPU spend with no number in front of it. */}
            {verify !== null && (verify.counts.gpu > 0 || verify.counts.stitch > 0) ? (
              <button
                type="button"
                className="ab-btn ab-btn-sm"
                disabled={busy}
                onClick={() => void runFill(fill !== null && !fill.applied)}
              >
                {busy ? "Working…" : fill !== null && !fill.applied ? "Yes, queue them" : "Fill the gaps…"}
              </button>
            ) : null}
          </span>
          {error !== null ? <p className="ab-hint-bad">{error}</p> : null}
        </td>
      </tr>

      {book.sweep !== null ? (
        <tr>
          <td className="ab-table-detail" colSpan={6} data-label="Sweep">
            <span className="ab-chip">{book.sweep.state}</span> whole-book sweep · cursor at sentence{" "}
            {book.sweep.cursorOrd.toLocaleString()} · {describeGpu(book.sweep.gpuSecSpent)} spent · started by{" "}
            {book.sweep.startedBy} {timeAgo(book.sweep.startedAt)} · moved {timeAgo(book.sweep.updatedAt)}
          </td>
        </tr>
      ) : null}

      {verify !== null ? (
        <tr>
          <td className="ab-table-detail" colSpan={6} data-label="Verified">
            <VerifyView report={verify} />
            {fill === null ? null : (
              <p className={fill.applied ? "ab-hint" : "ab-hint-bad"}>
                <strong>{fill.summary}</strong>
                {fill.skipped.length === 0 ? null : (
                  <>
                    {" "}
                    Skipped: {fill.skipped.map((s) => `${s.pageId} (${s.why})`).join(" · ")}
                  </>
                )}
              </p>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function VerifyView({ report }: { report: VerifyReport }): ReactElement {
  const c = report.counts;
  // The server already sent only the unfinished pages — filtering again here would be a second
  // opinion about what "unfinished" means, and the two would disagree the day one of them changes.
  const unfinished = report.pages;

  return (
    <>
      <p className={report.complete ? "ab-hint" : "ab-hint-bad"}>
        <strong>{report.summary}</strong>
      </p>
      <dl className="ab-facts">
        <div>
          <dt>Ready</dt>
          <dd>{c.ready}</dd>
        </div>
        <div>
          <dt>Needs a re-stitch</dt>
          <dd>{c.stitch}</dd>
        </div>
        <div>
          <dt>Needs the GPU</dt>
          <dd>{c.gpu}</dd>
        </div>
        <div>
          <dt>Failed</dt>
          <dd>{c.failed}</dd>
        </div>
        <div>
          <dt>Nothing to read aloud</dt>
          <dd>{c.nothingToSay}</dd>
        </div>
        <div>
          <dt>Stale index rows</dt>
          <dd>{report.staleIndex}</dd>
        </div>
      </dl>
      {c.stitch > 0 ? (
        <p className="ab-hint">
          {c.stitch} page{c.stitch === 1 ? " is" : "s are"} half a second of CPU from done — every clip
          already exists and only the stitch is missing. That work goes to the <code>media</code> lane and
          costs no GPU.
        </p>
      ) : null}
      {report.staleIndex > 0 ? (
        // Not an error by itself — it is the pronunciation editor's or the renormaliser's footprint.
        // On a book nobody edited it means an invalidation path ran and did not finish.
        <p className="ab-hint-bad">
          {report.staleIndex} <code>sentence_clips</code> row{report.staleIndex === 1 ? "" : "s"} point at a
          key the sentence no longer has. On a book nobody edited that means an invalidation ran and
          stopped halfway.
        </p>
      ) : null}
      {unfinished.length === 0 ? null : (
        <table className="ab-table">
          <thead>
            <tr>
              <th scope="col">Page</th>
              <th scope="col">Needs</th>
              <th scope="col">Missing clips</th>
              <th scope="col">Cost</th>
            </tr>
          </thead>
          <tbody>
            {unfinished.map((p) => (
              <tr key={p.pageId}>
                <td data-label="Page">
                  <code className="ab-sid">{p.pageId}</code>
                </td>
                <td data-label="Needs">
                  <span className={p.state === "failed" ? "ab-chip ab-chip-bad" : "ab-chip"}>{p.state}</span>{" "}
                  <span className="ab-hint-quiet">planner says {p.planState}</span>
                </td>
                <td data-label="Missing clips" className="ab-num">
                  {p.missing}
                </td>
                <td data-label="Cost" className="ab-num">
                  {p.estGpuSec > 0 ? describeGpu(p.estGpuSec) : <span className="ab-hint-quiet">no GPU</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {report.pagesOmitted > 0 ? (
        <p className="ab-hint-quiet">
          …and {report.pagesOmitted} more unfinished page{report.pagesOmitted === 1 ? "" : "s"}. The counts
          above are over the whole edition; only the first {unfinished.length} rows travel.
        </p>
      ) : null}
      <p className="ab-hint-quiet">
        Checked {timeAgo(report.checkedAt)} in {report.tookMs} ms and never cached — this is a live re-plan
        of every page in the edition, which is why it is a button and not a column.
      </p>
    </>
  );
}

/* ── formatting, shared with the Backlog tab ──────────────────────────────── */

/** Minutes past 90 s, hours past 90 min. "About 154 seconds of spark-1" is not a number anyone weighs. */
export function describeGpu(sec: number): string {
  if (sec <= 0) return "none";
  if (sec < 90) return `${Math.round(sec)} s`;
  const min = sec / 60;
  if (min < 90) return `${Math.round(min)} min`;
  return `${Math.round((min / 60) * 10) / 10} h`;
}

export function describeBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  const units = ["B", "kB", "MB", "GB", "TB"];
  let n = bytes;
  let i = 0;
  while (n >= 1000 && i < units.length - 1) {
    n /= 1000;
    i++;
  }
  return `${n >= 100 || i === 0 ? Math.round(n) : Math.round(n * 10) / 10} ${units[i]}`;
}

export default Generations;
